import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { AuditFirm } from '../../../models/auditFirm';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ColumnConfig, LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-audit-firm-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './audit-firm-list.component.html',
  styleUrl: './audit-firm-list.component.scss'
})
export class AuditFirmListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  auditFirms = signal<AuditFirm[]>([]);
  isSearchVisible: boolean = false;
  matchedItems: AuditFirm[] | undefined = undefined;
  rightItem: AuditFirm | undefined = undefined;

  // Pagination state
  currentPage: number = 1;
  pageSize: number = 25;
  totalCount: number = 0;
  sortBy: string = 'name';
  ascending: boolean = true;

  // Search filter state
  searchFilter: LookupTableSearchFilter = {};

  auditFirmsColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true },
    { key: 'isInBigFour', header: 'Is in Big Four', type: 'boolean', sortable: true, editable: true },
    { key: 'isFirst', header: 'Is First', type: 'boolean', sortable: true, editable: true }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadAuditFirms();
  }

  loadAuditFirms(): void {
    this.spinnerService.show();

    // Check if any search filters are active
    const hasSearchFilters = this.searchFilter.name;

    if (hasSearchFilters) {
      // Use search endpoint when filters are active
      const searchRequest = {
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        sortBy: this.sortBy,
        ascending: this.ascending,
        ...this.searchFilter
      };

      this.lutService.searchAuditFirms(searchRequest).subscribe({
        next: (response) => {
          this.auditFirms.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading audit firms:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Audit Firms:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    } else {
      // Use regular paginated endpoint when no filters are active
      this.lutService.getAuditFirmsPaginated(
        this.currentPage,
        this.pageSize,
        this.sortBy,
        this.ascending
      ).subscribe({
        next: (response) => {
          this.auditFirms.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading audit firms:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Audit Firms:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1; // MatPaginator uses 0-based index
    this.pageSize = event.pageSize;
    this.loadAuditFirms();
  }

  onSortChange(sort: Sort): void {
    // If sort direction is cleared (empty string), reset to default sort
    if (!sort.direction) {
      this.sortBy = 'name';
      this.ascending = true;
    } else {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
    }

    this.loadAuditFirms();
  }

  onCreateAuditFirm(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    const { exactMatch, ...actualFilters } = this.searchFilter;
    return Object.values(actualFilters).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onItemSelected(auditFirm: AuditFirm): void {
  }

  onEditAuditFirm(auditFirm: AuditFirm): void {
  }

  onDeleteAuditFirm(auditFirm: AuditFirm): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(auditFirm.name)
      .subscribe(result => {
        if (result) {
          const originalAuditFirms = this.auditFirms();

          this.auditFirms.update(auditFirms => auditFirms.filter(af => af.id !== auditFirm.id));

          this.spinnerService.show();
          this.lutService.deleteAuditFirm(auditFirm.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Audit firm deleted successfully');
              // Reload current page
              this.loadAuditFirms();
            },
            error: (error) => {
              console.error('Error deleting audit firm:', error);
              this.spinnerService.hide();
              this.auditFirms.set(originalAuditFirms);
              this.toast.error(`<strong>Failed to delete Audit Firm:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: AuditFirm, newName: string} | {item: AuditFirm, newValue: any, columnKey: string}): void {
    const originalItem = { ...event.item };

    let updatedItem: AuditFirm;
    if ('newName' in event) {
      updatedItem = { ...event.item, name: event.newName };
    } else {
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    this.auditFirms.update(auditFirms =>
      auditFirms.map(af => af.id === event.item.id ? updatedItem : af)
    );

    this.spinnerService.show();
    this.lutService.updateAuditFirm(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit firm updated successfully');
        this.auditFirms.update(auditFirms =>
          auditFirms.map(af => af.id === event.item.id ? response : af)
        );
      },
      error: (error) => {
        console.error('Error updating audit firm:', error);
        this.spinnerService.hide();
        this.auditFirms.update(auditFirms =>
          auditFirms.map(af => af.id === event.item.id ? originalItem : af)
        );
        this.toast.error(`<strong>Failed to update Audit Firm:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    const tempId = crypto.randomUUID();
    const newAuditFirm: AuditFirm = {
      id: tempId,
      name: event['name'],
      isInBigFour: event['isInBigFour'] ?? false,
      isFirst: event['isFirst'] ?? false,
      systemReserved: false
    };

    this.auditFirms.update(auditFirms => [newAuditFirm, ...auditFirms]);

    this.spinnerService.show();
    this.lutService.createAuditFirm({
      name: event['name'],
      isInBigFour: event['isInBigFour'] ?? false,
      isFirst: event['isFirst'] ?? false,
      systemReserved: false
    }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit firm created successfully');
        this.auditFirms.update(auditFirms =>
          auditFirms.map(af => af.id === tempId ? response : af)
        );
      },
      error: (error) => {
        console.error('Error creating audit firm:', error);
        this.spinnerService.hide();
        this.auditFirms.update(auditFirms =>
          auditFirms.filter(af => af.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Audit Firm:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {matchedItems?: AuditFirm[], rightItem?: AuditFirm}): void {
    this.matchedItems = event.matchedItems;
    this.rightItem = event.rightItem;
  }

  onAuditFirmCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalAuditFirms = this.auditFirms();

        this.auditFirms.update(auditFirms =>
          auditFirms.filter(af => !bulkMergeRequest.leftIds.includes(af.id))
        );

        this.rightItem = undefined;
        this.matchedItems = undefined;

        this.spinnerService.show();
        this.lutService.mergeAuditFirmsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Audit firms merged successfully');
            this.lookupTable.clearMatchingState();
            this.loadAuditFirms();
          },
          error: (error) => {
            console.error('Error merging audit firms:', error);
            this.spinnerService.hide();
            this.auditFirms.set(originalAuditFirms);
            this.rightItem = originalAuditFirms.find(af => af.id === bulkMergeRequest.rightId);
            this.matchedItems = originalAuditFirms.filter(af => bulkMergeRequest.leftIds.includes(af.id));
            this.toast.error(`<strong>Failed to merge Audit Firms:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.currentPage = 1; // Reset to first page when searching

    // Reset the paginator to first page visually
    if (this.lookupTable?.['paginator']) {
      this.lookupTable['paginator'].pageIndex = 0;
    }

    this.loadAuditFirms();
  }
}
